import LoadingButton from '@mui/lab/LoadingButton';
import {
    Alert,
    Box,
    FormControlLabel,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import useHandleErrors from '../../../hooks/useHandleErrors';
import server from '../../../global/server';
import { emReais } from '../../../global/repasse';
import { useSnackbar } from 'notistack';
import { useFetch } from '../../../hooks/useFetch';
import PercentIcon from '@mui/icons-material/Percent';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import UpdateIcon from '@mui/icons-material/Update';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const SectionCard = ({ children, icon, title, hint }) => (
    <Box sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: '12px',
        border: '1px solid #f0f0f0',
        bgcolor: '#fafafa',
    }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            {icon}
            <Typography variant="subtitle2" fontWeight={600} color="#333">
                {title}
            </Typography>
        </Stack>
        {hint && (
            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 2 }}>
                <HelpOutlineIcon sx={{ fontSize: 14, color: '#999', mt: 0.2 }} />
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {hint}
                </Typography>
            </Stack>
        )}
        {children}
    </Box>
);

// Valor que o backoffice mostra mas nao deixa editar: quem o define esta fora
// da Empiria. Mostrar como campo editavel dava a entender que o admin escolhe
// o numero, quando na pratica ele so espelha o que o fornecedor cobra.
const ValorFixo = ({ valor, origem }) => (
    <Box>
        <Typography variant="h6" fontWeight={700} color="#333" sx={{ lineHeight: 1.2 }}>
            {valor}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {origem}
        </Typography>
    </Box>
);

export default () => {
    const [errs, setErrs] = useState();
    useHandleErrors(errs);

    const { data, error, mutate } = useFetch('/backoffice/settings');
    useHandleErrors(error);

    const { enqueueSnackbar } = useSnackbar();

    const { values, errors, dirty, handleChange, handleSubmit, handleBlur, isSubmitting, setValues, setFieldValue, resetForm } =
        useFormik({
            initialValues: {
                // call_cost_fee e pagarme_fee ficam de fora: vem dos
                // fornecedores e a tela so os exibe
                empiria_fee: '',
                front_version: '',
                min_cost: '',
                auto_approve: true,
            },
            onSubmit: async (values, { setSubmitting }) => {
                try {
                    setSubmitting(true);
                    // So os campos que a Empiria decide. Custo de chamada e taxa
                    // do Pagar.me vem dos fornecedores e nao sao editaveis aqui:
                    // manda-los de volta so criaria a chance de sobrescreve-los
                    // com um valor digitado por engano.
                    await server.put('/backoffice/settings', {
                        empiria_fee: values.empiria_fee,
                        min_cost: values.min_cost,
                        front_version: values.front_version,
                        auto_approve: values.auto_approve,
                    });
                    enqueueSnackbar('Configurações atualizadas com sucesso!', { variant: 'success' });
                    mutate();
                    setSubmitting(false);
                } catch (error) {
                    setSubmitting(false);
                    setErrs(error);
                }
            },
        });

    useEffect(() => {
        if (data) {
            // So os campos editaveis: jogar a resposta inteira no formulario
            // traria os valores dos fornecedores e os campos de agendamento,
            // que nao sao editados aqui e bagunçam o controle de "alterado"
            const editaveis = {
                empiria_fee: data.empiria_fee ?? '',
                min_cost: data.min_cost ?? '',
                front_version: data.front_version ?? '',
                auto_approve: data.auto_approve ?? true,
            };
            setValues(editaveis, false);
            resetForm({ values: editaveis });
        }
    }, [data]);

    // Mudanca de taxa fica pendente ate cumprir o aviso previo de 30 dias
    const taxaAgendada =
        data?.pending_empiria_fee !== null && data?.pending_empiria_fee !== undefined && data?.pending_fee_at
            ? {
                  percentual: Number(data.pending_empiria_fee),
                  quando: new Date(data.pending_fee_at).toLocaleDateString('pt-BR'),
              }
            : null;

    return (
        <Stack spacing={2.5}>
            <SectionCard
                icon={<VerifiedUserIcon sx={{ fontSize: 18, color: '#006a61' }} />}
                title="Aprovação de anfitriões"
                hint="Ligado: quem se cadastra como anfitrião já fica aprovado e seus encontros aparecem na plataforma. Desligado: o cadastro fica pendente de análise no backoffice — o anfitrião continua podendo criar encontros, mas eles só ficam visíveis para os outros usuários depois da aprovação."
            >
                <FormControlLabel
                    control={
                        <Switch
                            name="auto_approve"
                            checked={Boolean(values.auto_approve)}
                            onChange={(e) => setFieldValue('auto_approve', e.target.checked)}
                        />
                    }
                    label={
                        <Typography variant="body2" color="#333">
                            {values.auto_approve ? 'Aprovação automática' : 'Aprovação manual'}
                        </Typography>
                    }
                />
            </SectionCard>

            <SectionCard
                icon={<PercentIcon sx={{ fontSize: 18, color: '#006a61' }} />}
                title="Taxa de Intermediação"
                hint="Porcentagem que a Empiria cobra sobre cada venda de encontro pago, descontada automaticamente via split no Pagar.me. A Política Geral de Compra e Pagamento exige aviso prévio de 30 dias para mudar esse valor: ao salvar, a nova porcentagem fica AGENDADA e só passa a valer depois do prazo. Até lá as vendas continuam saindo pela taxa atual e os anfitriões veem o aviso na tela de criação de encontro."
            >
                <Stack spacing={1.5}>
                    <ValorFixo
                        valor={data ? `${data.empiria_fee}%` : '—'}
                        origem="Em vigor hoje. É esta a porcentagem usada no split de cada venda."
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="Nova porcentagem"
                        type="number"
                        name="empiria_fee"
                        value={values.empiria_fee}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(errors.empiria_fee)}
                        helperText={
                            errors.empiria_fee ||
                            'Ao salvar, o valor fica agendado e passa a valer 30 dias depois. Salvar a porcentagem em vigor cancela um agendamento.'
                        }
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                    />

                    {taxaAgendada && (
                        <Alert severity="info" sx={{ borderRadius: '10px' }}>
                            Mudança agendada: a taxa passa de {data?.empiria_fee}% para{' '}
                            <strong>{taxaAgendada.percentual}%</strong> em{' '}
                            <strong>{taxaAgendada.quando}</strong>.
                        </Alert>
                    )}
                </Stack>
            </SectionCard>

            <SectionCard
                icon={<PhoneInTalkIcon sx={{ fontSize: 18, color: '#006a61' }} />}
                title="Custo fixo por chamada"
                hint="Quanto o fornecedor de videochamada cobra por encontro realizado. A Empiria repassa esse custo: ele é somado à Taxa de Intermediação para compor o valor retido em cada venda. Quem define o número é o fornecedor, então ele não é editável aqui — mudou o contrato, a equipe de desenvolvimento atualiza."
            >
                <ValorFixo
                    valor={data ? emReais(data.call_cost_fee) : '—'}
                    origem="Definido pelo fornecedor da videochamada."
                />
            </SectionCard>

            <SectionCard
                icon={<PercentIcon sx={{ fontSize: 18, color: '#006a61' }} />}
                title="Taxa estimada do Pagar.me"
                hint="Usada apenas para MOSTRAR ao anfitrião quanto ele deve receber. Não entra em nenhuma cobrança: no split quem paga o processamento é o anfitrião, e o valor real vem do Pagar.me conforme a forma de pagamento e o parcelamento. Quem define a alíquota é o Pagar.me, então ela não é editável aqui."
            >
                <ValorFixo
                    valor={data ? `~${String(data.pagarme_fee).replace('.', ',')}%` : '—'}
                    origem="Estimativa da alíquota do Pagar.me. Só para exibição."
                />
            </SectionCard>

            <SectionCard
                icon={<AttachMoneyIcon sx={{ fontSize: 18, color: '#006a61' }} />}
                title="Preço mínimo de encontro"
                hint="Valor mínimo que um anfitrião pode cobrar por um encontro pago. Garante que, após descontar a comissão e o custo de chamada, o anfitrião ainda receba um valor positivo."
            >
                <TextField
                    fullWidth
                    size="small"
                    label="Valor mínimo"
                    type="number"
                    name="min_cost"
                    placeholder="1.00"
                    value={values.min_cost}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.min_cost)}
                    helperText={
                        errors.min_cost ||
                        (data ? `Em vigor hoje: ${emReais(data.min_cost)}` : 'Valor mínimo por encontro pago')
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                />
            </SectionCard>

            <SectionCard
                icon={<UpdateIcon sx={{ fontSize: 18, color: '#006a61' }} />}
                title="Versão do frontend"
                hint="Ao alterar este valor, todos os usuários serão forcados a recarregar a página na próxima vez que acessarem a plataforma. Use após um deploy para garantir que ninguém fique com cache antigo."
            >
                <TextField
                    fullWidth
                    size="small"
                    label="Versão"
                    name="front_version"
                    value={values.front_version}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.front_version)}
                    helperText={errors.front_version || 'Padrao: 1.0.0'}
                    placeholder="1.0.0"
                    InputLabelProps={{ shrink: true }}
                />
            </SectionCard>

            <LoadingButton
                onClick={handleSubmit}
                loading={isSubmitting}
                variant="contained"
                fullWidth
                disabled={!dirty || isSubmitting}
                sx={{
                    bgcolor: '#006a61',
                    borderRadius: '8px',
                    height: 44,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    '&:hover': { bgcolor: '#005a52' },
                }}
            >
                Salvar configurações
            </LoadingButton>
        </Stack>
    );
};
